import { useState, useMemo } from "react";
import { useTestimonials, useSubmitTestimonial } from "../../../lib/api/testimonials.api";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { track } from "../../../lib/analytics";

type FormState = "idle" | "submitting" | "sent";

// Date d'ouverture des avis : 30 jours après le 14 mars 2026
const OPEN_DATE = new Date("2026-04-15T00:00:00");

function useCountdown() {
  const [now, setNow] = useState(() => Date.now());

  useState(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  });

  return useMemo(() => {
    const diff = OPEN_DATE.getTime() - now;
    if (diff <= 0) return { open: true, days: 0, hours: 0 };
    const days = Math.floor(diff / 86_400_000);
    const hours = Math.floor((diff % 86_400_000) / 3_600_000);
    return { open: false, days, hours };
  }, [now]);
}

export function TestimonialsSection() {
  const { data: testimonials } = useTestimonials();
  const submit = useSubmitTestimonial();
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.15, "testimonials");
  const countdown = useCountdown();

  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    setFormState("submitting");
    submit.mutate(
      { name: name.trim(), message: message.trim() },
      {
        onSuccess: () => {
          setFormState("sent");
          track("landing_testimonial_submitted", {});
          setName("");
          setMessage("");
        },
        onError: () => setFormState("idle"),
      },
    );
  };

  const hasTestimonials = testimonials && testimonials.length > 0;
  const formattedDate = OPEN_DATE.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <section className="py-16 px-6">
      <div
        ref={ref}
        className={`max-w-3xl mx-auto transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <button
          onClick={() => { const next = !isOpen; track("landing_testimonials_toggled", { opened: next }); setIsOpen(next); }}
          className="w-full flex items-center justify-between gap-4 group text-left"
        >
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-ink">
              Ce qu'en pensent les utilisateurs
            </h2>
            <p className="mt-2 text-ink-muted text-sm">
              Palnia s'améliore grâce à vos retours.
              Ouverture des avis le <span className="font-semibold text-ink whitespace-nowrap">{formattedDate}</span>.
            </p>
          </div>
          <span className="shrink-0 text-sm text-accent font-medium transition-colors">
            {isOpen ? "Voir moins" : "Voir plus"}
          </span>
        </button>

        <div
          className={`grid transition-all duration-500 ease-in-out ${
            isOpen ? "grid-rows-[1fr] opacity-100 mt-6" : "grid-rows-[0fr] opacity-0 mt-0"
          }`}
        >
          <div className="overflow-hidden">
            {!countdown.open && (
              <div className="flex items-center gap-6 p-5 bg-surface-alt border border-border rounded-xl">
                <div className="flex gap-4">
                  <div className="text-center">
                    <span className="text-2xl font-bold text-accent">{countdown.days}</span>
                    <p className="text-[10px] text-ink-faint uppercase tracking-wide mt-0.5">jours</p>
                  </div>
                  <div className="text-center">
                    <span className="text-2xl font-bold text-accent">{countdown.hours}</span>
                    <p className="text-[10px] text-ink-faint uppercase tracking-wide mt-0.5">heures</p>
                  </div>
                </div>
                <p className="text-sm text-ink-muted">
                  D'ici là, explorez l'app et faites-vous votre propre avis.
                </p>
              </div>
            )}

            {hasTestimonials && (
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="p-4 bg-surface border border-border rounded-xl"
                  >
                    <p className="text-sm text-ink leading-relaxed">
                      &laquo; {t.message} &raquo;
                    </p>
                    <p className="mt-3 text-xs font-medium text-ink-muted">
                      — {t.name}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {countdown.open && (
              <div className="mt-6 p-5 bg-surface-alt border border-border rounded-xl">
                {formState === "sent" ? (
                  <p className="text-sm text-success font-medium">
                    Merci pour votre retour ! Il sera visible après validation.
                  </p>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <p className="text-sm font-medium text-ink">
                      Partagez votre expérience
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Votre prénom"
                        required
                        maxLength={50}
                        className="sm:w-36 px-3 py-2.5 text-sm bg-surface border border-border text-ink
                          placeholder:text-ink-faint rounded-xl focus:outline-none
                          focus:ring-2 focus:ring-accent/30"
                      />
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Votre avis sur Palnia..."
                        required
                        maxLength={500}
                        className="flex-1 px-3 py-2.5 text-sm bg-surface border border-border text-ink
                          placeholder:text-ink-faint rounded-xl focus:outline-none
                          focus:ring-2 focus:ring-accent/30"
                      />
                      <button
                        type="submit"
                        disabled={formState === "submitting" || !name.trim() || !message.trim()}
                        className="px-5 py-2.5 text-sm font-semibold text-white bg-accent
                          hover:bg-accent-hover disabled:opacity-40 rounded-xl
                          transition-colors whitespace-nowrap"
                      >
                        Envoyer
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
